import type { CountryMetricRow } from "../types";
import {
  countryCodeToName,
  isUnknownCountryCode,
  largestRemainderPercentages,
} from "../utils";

/** Countries below this value are grouped into the "Other" bucket. */
export const COUNTRY_METRIC_THRESHOLD = 5;

/** Groups every unrecognised / untracked location code under one row. */
const UNKNOWN_KEY = "__unknown__";

/** A single country's value for whichever metric the card visualises. */
export interface CountryMetricEntry {
  country: string;
  value: number;
}

export interface BuildCountryMetricRowsParams {
  entries: CountryMetricEntry[];
  /**
   * Authoritative total used to recover values masked from the country response.
   *
   * Only pass a total whose metric definition, filters, scope and freshness are
   * guaranteed to match `entries`. Do not combine independently scoped totals —
   * the difference is rendered as a factual geographic bucket, so a mismatch
   * becomes a wrong number rather than a missing one.
   */
  total?: number;
  threshold?: number;
  /** When true, no bucketing: every country is shown on its own row. */
  revealSmallValues?: boolean;
  /**
   * What to render when the response carries no value at all:
   * - `"otherRow"` — a zeroed "Other" row (sessions / hours cards)
   * - `"none"`     — no rows, so the card shows its empty state
   */
  emptyState?: "none" | "otherRow";
  otherLabel: string;
  unknownLabel: string;
}

interface NormalizedEntry {
  name: string;
  isUnknown: boolean;
  value: number;
}

/**
 * Collapses the response to one entry per distinct display name, so duplicate
 * or aliased codes (e.g. both `DE` and `Germany`) and several different
 * untracked-location codes can never render as duplicate rows.
 */
const normalizeEntries = (
  entries: CountryMetricEntry[],
  unknownLabel: string,
): NormalizedEntry[] => {
  const byKey = new Map<string, NormalizedEntry>();

  for (const entry of entries) {
    const isUnknown = isUnknownCountryCode(entry.country);
    const key = isUnknown ? UNKNOWN_KEY : countryCodeToName(entry.country);
    const existing = byKey.get(key);

    if (existing) {
      existing.value += entry.value;
    } else {
      byKey.set(key, {
        name: isUnknown ? unknownLabel : key,
        isUnknown,
        value: entry.value,
      });
    }
  }

  return [...byKey.values()];
};

/** Descending by value, then by name so equal values keep a stable order. */
const byValueDesc = (a: NormalizedEntry, b: NormalizedEntry): number =>
  b.value - a.value || a.name.localeCompare(b.name);

const sumValues = (entries: NormalizedEntry[]): number =>
  entries.reduce((sum, entry) => sum + entry.value, 0);

/**
 * Turns per-country metric values into the display rows shared by all three
 * country cards (user sessions, hours in VR, certifications).
 *
 * Rules:
 * - Countries with no value are never shown individually.
 * - Countries at or above `threshold` are listed individually, descending.
 * - Countries below it — and every untracked location — are aggregated into a
 *   trailing "Other" row, whose percentage is `-1` ("N/A") while its own
 *   aggregate is still below the threshold.
 * - Percentages use the Largest Remainder Method so they never over-sum.
 * - `revealSmallValues` disables all of the above bucketing.
 */
export const buildCountryMetricRows = ({
  entries,
  total,
  threshold = COUNTRY_METRIC_THRESHOLD,
  revealSmallValues = false,
  emptyState = "otherRow",
  otherLabel,
  unknownLabel,
}: BuildCountryMetricRowsParams): CountryMetricRow[] => {
  const normalized = normalizeEntries(entries, unknownLabel);
  const withValue = normalized.filter((entry) => entry.value > 0);

  // ── No thresholds: every country gets its own row ──
  if (revealSmallValues) {
    const sorted = withValue.toSorted(byValueDesc);
    const revealedPercentages = largestRemainderPercentages(
      sorted.map((entry) => entry.value),
      sumValues(sorted),
    );

    return sorted.map((entry, i) => ({
      name: entry.name,
      metricValue: entry.value,
      percentage: revealedPercentages[i]!,
      isOther: false,
      isUnknown: entry.isUnknown,
    }));
  }

  const visibleTotal = sumValues(withValue);
  const authoritativeTotal = total && total > 0 ? total : 0;

  // ── Nothing to show ──
  if (visibleTotal === 0 && authoritativeTotal === 0) {
    if (emptyState === "none") return [];

    // A response with no rows at all is a definite zero, so its share is the
    // whole (empty) picture. A response whose rows were all privacy-masked has
    // a real but unknowable share, so it must read as N/A instead.
    return [
      {
        name: otherLabel,
        metricValue: 0,
        percentage: entries.length === 0 ? 100 : -1,
        isOther: true,
        otherCountries: [],
      },
    ];
  }

  const above = withValue
    .filter((entry) => !entry.isUnknown && entry.value >= threshold)
    .toSorted(byValueDesc);
  const below = withValue
    .filter((entry) => entry.isUnknown || entry.value < threshold)
    .toSorted(byValueDesc);

  const aboveTotal = sumValues(above);

  // A supplied total larger than what the response exposes means the remainder
  // was privacy-masked, so it belongs to "Other".
  const otherTotal =
    authoritativeTotal > aboveTotal
      ? authoritativeTotal - aboveTotal
      : sumValues(below);

  const effectiveTotal = aboveTotal + otherTotal;
  const hasRealOtherPct = otherTotal >= threshold;

  const percentages = largestRemainderPercentages(
    [
      ...above.map((entry) => entry.value),
      ...(hasRealOtherPct ? [otherTotal] : []),
    ],
    effectiveTotal,
  );

  const countryRows: CountryMetricRow[] = above.map((entry, i) => ({
    name: entry.name,
    metricValue: entry.value,
    percentage: percentages[i]!,
    isOther: false,
  }));

  const otherRow: CountryMetricRow = {
    name: otherLabel,
    metricValue: otherTotal,
    percentage: hasRealOtherPct ? percentages[above.length]! : -1,
    isOther: true,
    // Read by the CSV export, which lists the grouped countries as sub-rows.
    otherCountries: below.map((entry) => ({
      name: entry.name,
      metricValue: entry.value,
    })),
  };

  return [...countryRows, otherRow];
};
